import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../../components/apiconfig/api-config';

const roles = ['account', 'purchase', 'sale', 'finance', 'admin', 'manager', 'auditor', 'backup'];

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/auth/user-list`, { withCredentials: true });
      setUsers(res.data);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditClick = (user) => {
    setEditUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      is_active: user.is_active ? 1 : 0,
    });
    setEditError('');
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError('');
    setEditLoading(true);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        is_active: editForm.is_active,
      };
      if (editForm.password) payload.password = editForm.password;
      await axios.put(`${API_BASE_URL}/api/auth/user-list/${editUser.id}`, payload, { withCredentials: true });
      setEditUser(null);
      setEditForm({});
      fetchUsers();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditUser(null);
    setEditForm({});
    setEditError('');
  };

  return (
    <div className="max-w-8xl mx-auto p-3 bg-white rounded-md shadow-sm border border-gray-300">
      <div className="flex flex-col sm:flex-row justify-between items-center pb-2 mb-3 border-b border-gray-200 gap-2">
        <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">User Records</h2>
        <button
          className="flex items-center justify-center gap-1.5 bg-[#15803d] text-white px-3 py-1.5 text-[12px] font-semibold rounded-sm hover:bg-[#166534] transition-colors shadow-sm active:translate-y-[1px]"
          onClick={() => navigate('/register')}
        >
          Add New User
        </button>
      </div>
      {loading ? (
        <div className="text-center text-[#15803d] py-6 text-[13px]">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-[13px]">{error}</div>
      ) : users.length === 0 ? (
        <div className="text-center text-gray-500 py-6 text-[13px]">No user records found.</div>
      ) : (
        <div className="overflow-x-auto border border-gray-300 rounded-sm">
          <table className="min-w-full text-left border-collapse">
            <thead className="bg-[#f4f4f4] border-b border-gray-300">
              <tr>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Name</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Email</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Role</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Active</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Last Login</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Created At</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {users.map((user, idx) => (
                <tr key={user.id} className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-200 last:border-b-0">
                  <td className="px-3 py-1 font-semibold">{user.name}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{user.email}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{user.role}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{user.is_active ? 'Active' : 'Inactive'}</td>
             <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">
  {user.last_login
    ? new Date(user.last_login).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // for 24-hour format; remove this line for 12-hour format
      })
    : '-'}
</td>
<td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">
  {new Date(user.created_at).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}
</td>

                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">
                    <button
                      className="bg-[#15803d] text-white px-3 py-1 rounded text-[12px] hover:bg-[#166534] transition-colors"
                      onClick={() => handleEditClick(user)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editUser && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <h3 className="text-[15px] font-semibold mb-4">Edit User</h3>
            {editError && <div className="mb-2 p-2 bg-red-100 text-red-700 rounded">{editError}</div>}
            <form className="space-y-4" onSubmit={handleEditSubmit}>
              <input
                type="text"
                name="name"
                value={editForm.name}
                onChange={handleEditChange}
                placeholder="Full Name"
                required
                className="w-full px-3 py-1.5 border rounded"
              />
              <input
                type="email"
                name="email"
                value={editForm.email}
                onChange={handleEditChange}
                placeholder="Email"
                required
                className="w-full px-3 py-1.5 border rounded"
              />
              <input
                type="password"
                name="password"
                value={editForm.password}
                onChange={handleEditChange}
                placeholder="New Password (leave blank to keep unchanged)"
                className="w-full px-3 py-1.5 border rounded"
              />
              <select
                name="role"
                value={editForm.role}
                onChange={handleEditChange}
                required
                className="w-full px-3 py-1.5 border rounded"
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                ))}
              </select>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={!!editForm.is_active}
                  onChange={handleEditChange}
                  className="mr-2"
                />
                <label htmlFor="is_active" className="text-[13px]">Active</label>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="submit"
                  className="bg-[#15803d] text-white px-3 py-1.5 rounded hover:bg-[#166534]"
                  disabled={editLoading}
                >
                  {editLoading ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="bg-gray-300 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-400"
                  onClick={handleEditCancel}
                  disabled={editLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
